import React, { Fragment, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { get } from "lodash";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import Collapse from "./Collapse";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getImageUrl } from "utils";
import getFileExtension from "utils/getFileExtension";
import removeExtension from "utils/removeExtension";

import pdfImg from "assets/images/pdf.png";

const docFormats = ["doc", "docx"];

const FireSafetyRenewalView = ({
  fscrCertificatesDetails,
  isExpandable = true,
  t,
}) => {
  const [documentToPreview, setDocumentToPreview] = useState(null);

  const previewButtonHandler = (name, documentURL) => {
    const fileExtension = getFileExtension(name ?? "");
    const url = getImageUrl(documentURL ?? "");

    if (docFormats.includes(fileExtension?.toLowerCase())) {
      toast.success("Document downloaded successfully");
    } else {
      setDocumentToPreview({
        documentTypeName: "",
        url,
        documentType: fileExtension,
        documentName: removeExtension(name ?? "Document"),
      });
    }
  };

  const fields = useMemo(
    () => [
      {
        name: "Renewal Number",
        key: "renewalNumber",
        isRequired: true,
      },
      {
        name: "Date Of Issue",
        key: "dateOfIssue",
        renderField: (value) =>
          value
            ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY")
            : "N/A",
        isRequired: true,
      },
      {
        name: "Due Date",
        key: "dueDate",
        renderField: (value) =>
          value
            ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY")
            : "N/A",
        isRequired: true,
      },
    ],
    []
  );

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Collapse
        title="Fire Safety Certificate Renewal"
        isExpandable={isExpandable}
      >
        {fscrCertificatesDetails?.map((item, index) => (
          <Fragment key={item.id}>
            <Row className="mb-2">
              <label className="fw-semibold">
                {t("Fire Safety Certificate Renewal")} : {index + 1}
              </label>

              {fields.map(({ name, key, isRequired, renderField }) => {
                const value = get(item, key, "");
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : value ?? (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              })}
            </Row>

            {/* Document Section */}
            {!!item?.url && (
              <Row className="mb-3">
                <Col lg={12}>
                  <label >
                    {t("Renewal Certificate Document:")}
                  </label>
                  <div className="image-container">
                    <a
                      onClick={() =>
                        previewButtonHandler(
                          item?.url,
                          item?.url
                        )
                      }
                    >
                      <Box
                        component="img"
                        className="image d-block cursor-pointer"
                        alt="document image"
                        src={
                          getFileExtension(
                            (item?.url ?? "").toLowerCase()
                          ) === "pdf"
                            ? pdfImg
                            : getImageUrl(item?.url)
                        }
                      />
                    </a>
                  </div>
                </Col>
              </Row>
            )}
          </Fragment>
        ))}
      </Collapse>
    </>
  );
};

FireSafetyRenewalView.propTypes = {
  fscrCertificatesDetails: PropTypes.array,
  isExpandable: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyRenewalView);
